<template>
  <slot name="loading" v-if="status === statuses.loading && paginationType !== 'load more'">
    <div :class="['absolute top-6 left-1/2 -translate-x-1/2 z-10 flex items-center', searched ? 'left-1/2' : 'left-1/2 md:left-1/3']">
      <svg class="h-5 w-5 mr-1 motion-safe:animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      {{ loadingLabel }}
    </div>
  </slot>
  
  <slot v-if="items && items.length > 0" :items="items">
    {{ JSON.stringify(items) }}
  </slot>

  <slot name="pre-search" v-if="status === statuses.preSearch">
    {{ preSearchLabel }}
  </slot>

  <slot name="no-results" v-if="status === statuses.noResults">
    {{ noResultsLabel }}
  </slot>

  <slot name="error" v-if="status === statuses.error">
    {{ errorLabel }}
  </slot>
</template>

<script>
  import QueryStringLibrary from 'qs'
  
  import Base from './../base'
  import Events from './../../events'
  import EventBus from './../../../../bus'
  import EventsSearch from '../../../search/events'
  
  const defaultPageSize = 8
  
  export default {
    name: "IntNewsListingDisplay",
  
    mixins: [Base],
  
    props: {
      fetchUrl: { type: String, required: true },
      initialQuery: { type: Object, required: true },
      initialItems: { type: Array, default: [] },
      fetchOnLoad: { type: Boolean, default: true },
      paginationType: {
        type: String, default: 'none',
        validator(value) {
          return ['pagination', 'load more', 'none'].includes(value)
        }
      },
      hasSearched: { type: Boolean, default: false },
      preSearchLabel: { type: String, default: 'You have not searched yet' },
      loadingLabel: { type: String, default: 'Loading...' },
      noResultsLabel: { type: String, default: 'No results were found' },
      errorLabel: { type: String, default: 'There was an error fetching the results' },
      anchorId: { type: String, default: 'news-listing' }
    },
  
    data() {
      return {
        items: this.initialItems,
        query: Object.assign({
              pageNumber: this.initialQuery.pageNumber || 1,
              pageSize: this.initialQuery.pageSize || defaultPageSize,
              nodeId: this.initialQuery.nodeId || 0,
              direction: this.initialQuery.direction || 'next'
            },
            this.initialQuery),
        searched: this.hasSearched,
        statuses: {
          loading: 'loading',
          preSearch: 'pre-search',
          noResults: 'no-results',
          error: 'error',
          ok: 'ok'
        },
        status: null,
        replaceOnFetch: this.paginationType === 'pagination'
      }
    },
  
    computed: {
      queryUrl() {
        let query = QueryStringLibrary.stringify(this.query, { skipNulls: true })
        return `${this.fetchUrl}?${query}`
      }
    },
  
    created() {
      this.status = this.getInitialStatus()
      EventBus.$emit(EventsSearch.statusUpdated, this.status)
      
      EventBus.$on(EventsSearch.searchTermUpdated, this.onSearchTermUpdatedEvent)
      EventBus.$on(Events.pageNumberUpdated, this.onPageNumberOrFiltersUpdatedEvent)
      EventBus.$on(EventsSearch.filtersUpdated, this.onPageNumberOrFiltersUpdatedEvent)
    },
  
    mounted() {
      if (this.fetchOnLoad) this.fetchItems(false)
    },
  
    methods: {
      onSearchTermUpdatedEvent(e) {
        if (e.group !== this.group) return
  
        this.query = Object.assign(this.query, e.data)
        
        delete this.query.categories
        delete this.query.sizes
        delete this.query.ranges
  
        this.fetchItems(this.replaceOnFetch)
      },
      
      onPageNumberOrFiltersUpdatedEvent(e) {
        if (e.group !== this.group) return
  
        this.query = Object.assign(this.query, e.data)
  
        this.fetchItems(this.replaceOnFetch)
      },
      
      getInitialStatus() {
        return this.fetchOnLoad
            ? this.statuses.loading
            : (this.items.length === 0
                ? (this.searched
                    ? this.statuses.noResults
                    : this.statuses.preSearch)
                : this.statuses.ok)
      },

      fetchItems(scroll = true) {
        this.status = this.statuses.loading
        EventBus.$emit(EventsSearch.statusUpdated, this.status)
        
        this.$nextTick(() => {
          fetch(this.queryUrl, { method: 'GET' })
              .then((response) => {
                return response.json()
              })
              .then((json) => {
                if (json.redirectUrl) {
                  window.location.href = json.redirectUrl
                  // Return is required otherwise other properties in the response are updated before the browser actions the redirect
                  return
                }
                
                if (this.paginationType === 'load more') {
                  if (this.query.direction === 'previous') this.items.unshift(...json.items)
                  else this.items.push(...json.items)
                } else {
                  this.items = json.items
                }
  
                this.setPagination(
                    this.query.pageNumber,
                    json.items.length || 0,
                    json.totalCount || 0,
                    this.query.pageSize,
                    json.filters || [])
  
                this.searched = true
                this.status = this.items.length === 0
                    ? this.statuses.noResults
                    : this.statuses.ok
                
                EventBus.$emit(EventsSearch.statusUpdated, this.status)
  
                if (scroll) this.scrollToElement(this.anchorId)
              })
              .catch((error) => {
                console.error(`${this.errorLabel}: ${error}`)
                
                this.status = this.statuses.error
                EventBus.$emit(EventsSearch.statusUpdated, this.status)
              })
        })
      },
  
      setPagination(pageNumber, itemCount, totalItemCount, pageSize, filters) {
        EventBus.$emit(Events.setPagination, { data: { pageNumber, itemCount: totalItemCount, pageSize }, group: this.group })
  
        const start = ((pageNumber - 1) * pageSize) + 1
        const end = (((pageNumber - 1) * pageSize) + Math.min(itemCount,pageSize))
        
        EventBus.$emit(
            Events.resultsFetched,
            {
              data: {
                start,
                end,
                total: totalItemCount,
                pageSize,
                query: this.query,
                display: itemCount > 0,
                filters: filters
              },
              group: this.group
            }
        )
      },
  
      scrollToElement(elementId) {
        const el = document.getElementById(elementId)
        if (el) el.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }
</script>